import dayjs from 'dayjs';
import {
  CGV2LocationOutDTO,
  CurrencyLockTypeEnum,
  GlobalOrdersRequest,
  LabelPreviewWithoutTypeOutDTO,
  LovDTO,
  NotifyTargetTypeEnum,
  TmplV2DetailOutDTO,
  TmplV2StopOutDTO,
  TemplateDistributionTypeEnum,
} from '@api/ringil-api.v2.tsx';
import { ExpeditionLocality, LocalityEntryModeEnum, LocationLockMode, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { DeadlineMode, toLocationNoteStops } from '@app/pages/expedition/create/mappers/detailToForm.ts';
import { CarrierDistributionSelectorData, DistributionType } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { fromContractedCarrierOutDTO, fromPriceTableDTO } from '@components/ringil3/Features/Contracted/types.ts';
import { applyLocationToLocality, initLineRoute } from '@app/pages/expedition/create/mappers/lineRoute.ts';
import { buildCarrierNoteWithLocationBlock } from '@utils/carrierNoteMerge.ts';
import { generateUUID, isNotBlank, isNotNullOrUndefined } from '@utils/utils.tsx';

/**
 * Linka (Template 2.0, kind LINE) → create-shipment form model.
 *
 * A line stop carries a set of candidate address-book locations: exactly one locks the stop to it,
 * several limit the choice to them (the user still has to pick one), none leaves the stop free. The
 * stop's weekly windows are not copied here — the timeslot editor reads them (entity TMPL_V2_STOP)
 * once the user picks the days. Orders get fresh ids so the shipment never shares them with the line.
 */

export interface LineToFormContext {
  defaultCurrencyIsoCode: string;
  defaultTimezoneId: string;
  deadlineMode: DeadlineMode;
  /** Response-deadline offset (hours) used when the line does not set its own interval. */
  effectiveHours: number;
  editDistributionAllowed: boolean;
  /** LADEN_FACTOR feature: the line capacity becomes the distribution laden factor. */
  ladenFactorEnabled: boolean;
  /** CONTRACTED_CARRIERS feature: without it the contracted distribution is not prefilled. */
  contractedEnabled: boolean;
  /** Current address-book data for the candidate locations, keyed by the id the line stores. */
  locationDataByLocationId: Map<string, CGV2LocationOutDTO>;
  /** Names for what the line stores as bare ids (people, folder, labels). */
  lookups?: {
    persons?: LovDTO[];
    folders?: LovDTO[];
    labels?: LabelPreviewWithoutTypeOutDTO[];
  };
  /** Injectable clock for the absolute deadline (tests). */
  now?: () => dayjs.Dayjs;
}

export const lockModeForStop = (stop: Pick<TmplV2StopOutDTO, 'candidateLocations'>): LocationLockMode => {
  const count = stop.candidateLocations?.length ?? 0;
  if (count === 1) return 'LOCKED';
  if (count > 1) return 'LIMITED';
  return 'FREE';
};

const cloneOrders = (orders: GlobalOrdersRequest[]): GlobalOrdersRequest[] =>
  (orders ?? []).map(order => ({
    ...order,
    items: (order.items ?? []).map(item => ({ ...item })),
  }));

/** The stop's candidates as the address book has them now: a superseded location is replaced by its successor. */
const currentCandidates = (stop: TmplV2StopOutDTO, ctx: LineToFormContext): CGV2LocationOutDTO[] =>
  (stop.candidateLocations ?? []).map(candidate => ctx.locationDataByLocationId.get(candidate.locationId) ?? candidate);

const mapStop = (stop: TmplV2StopOutDTO, index: number, ctx: LineToFormContext): ExpeditionLocality => {
  const lock = lockModeForStop(stop);
  const candidates = stop.candidateLocations ?? [];
  const orders = cloneOrders(stop.orders);

  const locality: ExpeditionLocality = {
    id: generateUUID(),
    localityMode: LocalityEntryModeEnum.FROM_LOCATIONS,
    timeslots: { dayWithInterval: [] },
    loadingReference: stop.loadingReference,
    orders: orders.length > 0 ? orders : null,
    unloadingOrderIds: index > 0 ? [...(stop.unloadingOrderIds ?? [])] : null,
    requirements: stop.requirements?.length ? [...stop.requirements] : undefined,
    manualAddress: {},
    location: null,
    notifySelectedPerson: false,
    tmplV2StopId: stop.id,
    tmplV2StopHasOpeningHours: isNotBlank(stop.openingHours?.id),
    tmplV2StopLocationNote: isNotBlank(stop.locationNote) ? stop.locationNote : undefined,
    locationLock: lock,
    allowedLocationIds: lock === 'LIMITED' ? Array.from(new Set(currentCandidates(stop, ctx).map(candidate => candidate.locationId))) : undefined,
  };

  if (lock === 'LOCKED') {
    const candidate = candidates[0];
    const current = ctx.locationDataByLocationId.get(candidate.locationId);
    applyLocationToLocality(locality, current ?? candidate, stop.locationNote);
    // The prefill resolved a superseded location to its replacement; say so on the stop.
    if (current && current.locationId !== candidate.locationId) {
      locality.supersededFromLocationName = candidate.locationName;
    }
  }

  return locality;
};

/** Fresh order ids on every stop, and the unloading references moved over to them. */
const remapOrderIds = (locations: ExpeditionLocality[]) => {
  const orderIdMap = new Map<string, string>();
  locations.forEach(loc => {
    loc.orders?.forEach(order => {
      const newId = generateUUID();
      if (order.id) orderIdMap.set(order.id, newId);
      order.id = newId;
    });
  });
  locations.forEach((loc, index) => {
    if (index > 0 && loc.unloadingOrderIds?.length) {
      loc.unloadingOrderIds = loc.unloadingOrderIds.map(oldId => orderIdMap.get(oldId) || oldId);
    }
  });
};

/**
 * A Linka stores folders as bare ids. Keeps only those in [accessible] (the folder LOV, RD-2977: only folders the
 * user may access), so a folder the user cannot see never shows as a nameless chip nor gets saved onto a shipment.
 */
export const pickAccessibleFolders = (ids: string[] | undefined, accessible: LovDTO[] | undefined): LovDTO[] =>
  (ids ?? []).map(id => accessible?.find(folder => folder.id === id)).filter((folder): folder is LovDTO => !!folder);

const lov = (id: string | undefined, list: LovDTO[] | undefined): LovDTO | null => {
  if (!id) return null;
  const found = list?.find(item => item.id === id);
  return { id, name: found?.name ?? '', active: found?.active ?? true };
};

/** The distribution a line prefills: CONTRACTED with its carriers, deadline = now + the line's interval. */
export const mapLineDistribution = (detail: TmplV2DetailOutDTO, ctx: LineToFormContext): CarrierDistributionSelectorData | undefined => {
  const distribution = detail.distribution;
  if (!ctx.editDistributionAllowed || !ctx.contractedEnabled || distribution?.templateDistributionType !== TemplateDistributionTypeEnum.CONTRACTED) return undefined;

  const hours = distribution.intervalHours > 0 ? distribution.intervalHours : ctx.effectiveHours;
  const priceTable = distribution.priceTable ? fromPriceTableDTO(distribution.priceTable) : undefined;
  const now = ctx.now ?? (() => dayjs());
  const ladenFactor = ctx.ladenFactorEnabled && detail.capacity?.ladenFactor > 0 ? detail.capacity.ladenFactor : undefined;

  return {
    distributionType: DistributionType.CONTRACTED,
    selectedItems: [],
    contractedCarriers: priceTable ? (distribution.contractedCarriers ?? []).map(c => fromContractedCarrierOutDTO(c, priceTable)) : [],
    // a copy for this shipment: plan / prices edited here never reach the line
    contractedPriceTable: priceTable,
    // Never sent for CONTRACTED; kept so switching to another type starts from a sane price.
    price: { price: 0, currency: ctx.defaultCurrencyIsoCode },
    responseDeadlineMode: ctx.deadlineMode,
    responseDeadlineOffsetValue: hours,
    responseDeadlineOffsetUnit: 'hours',
    responseDeadlineLocalTime: ctx.deadlineMode === 'absolute' ? now().add(hours, 'hour') : undefined,
    responseDeadlineTimezone: ctx.defaultTimezoneId,
    notifyAssignedCarrier: false,
    currencyLockType: CurrencyLockTypeEnum.NONE,
    ladenFactor: ladenFactor !== undefined ? true : undefined,
    ladenFactorValue: ladenFactor,
    allowPartialDelivery: detail.allowPartialDelivery,
  };
};

/**
 * Writes the line into `draft` (a blank form). Returns the distribution it prefilled, for the carrier
 * selector's "load from original" — `undefined` when none was.
 */
export const mapLineToForm = (detail: TmplV2DetailOutDTO, draft: NewExpeditionForm, ctx: LineToFormContext): CarrierDistributionSelectorData | undefined => {
  const header = detail.header;
  const internalInfo = detail.internalInfo;

  draft.tmplV2Id = detail.id;
  draft.tmplV2Name = detail.name;

  draft.expeditionType = header?.expeditionType;
  draft.transportationType = header?.transportationType;
  draft.truckTypeCode = header?.truckTypeCode;
  draft.loadingTypes = header?.loadingTypes || [];
  draft.specialRequirements = header?.requirements || [];
  if (header?.cargoType?.cargoTypeCode) {
    draft.cargoType = { cargoTypeCode: header.cargoType.cargoTypeCode, adrWeight: header.cargoType.adrWeight };
  }
  if (ctx.ladenFactorEnabled && detail.capacity?.ladenFactor > 0) {
    draft.defaultLadenFactor = detail.capacity.ladenFactor;
  }

  const stops = [...(detail.stops ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  if (stops.length > 0) {
    draft.locations = stops.map((stop, index) => mapStop(stop, index, ctx));
    remapOrderIds(draft.locations);
    if (detail.bothDirections && stops.length >= 2) {
      initLineRoute(
        draft,
        stops.map(stop => ({
          tmplV2StopId: stop.id,
          tmplV2StopHasOpeningHours: isNotBlank(stop.openingHours?.id),
          loadingReference: stop.loadingReference,
          requirements: stop.requirements?.length ? [...stop.requirements] : undefined,
          locationNote: stop.locationNote,
          candidates: currentCandidates(stop, ctx),
        })),
      );
    }
  }

  draft.carrierNote = buildCarrierNoteWithLocationBlock(header?.carrierNote || '', toLocationNoteStops(draft.locations));

  const labels = ctx.lookups?.labels ?? [];
  draft.labels = (internalInfo?.labelIds ?? [])
    .map(labelId => labels.find(label => label.labelId === labelId))
    .filter(isNotNullOrUndefined)
    .map(label => ({ labelId: label.labelId, labelValue: label.labelValue, labelColor: label.labelColor }));

  if (internalInfo) {
    draft.person = lov(internalInfo.responsiblePersonId, ctx.lookups?.persons);
    draft.disponentPerson = lov(internalInfo.disponentPersonId, ctx.lookups?.persons);
    draft.notifiedTargets = internalInfo.notifiedTargets?.length
      ? internalInfo.notifiedTargets.map(target => ({ ...target }))
      : (internalInfo.notifiedPersonIds ?? []).map(id => ({ id, notifyTargetType: NotifyTargetTypeEnum.USER }));
    draft.notifyByRole = isNotBlank(internalInfo.notifyByRole) && internalInfo.notifyByRole !== 'false' ? true : undefined;
    draft.invoiceState = internalInfo.invoiceState;
    draft.pairingSymbol = internalInfo.pairingSymbol;
    draft.internalNote = internalInfo.internalNote;
    draft.rebilling = internalInfo.rebilling
      ? {
          invoiceRebillingType: internalInfo.rebilling.invoiceRebillingType,
          invoiceDepartment: internalInfo.rebilling.invoiceDepartment,
          invoicePartner: internalInfo.rebilling.invoicePartnerId ? { companyId: internalInfo.rebilling.invoicePartnerId, companyName: '' } : undefined,
        }
      : null;
    // an empty result keeps whatever the form holds (e.g. the single accessible folder pre-filled for folderRequired)
    const folders = pickAccessibleFolders(internalInfo.folderIds, ctx.lookups?.folders);
    if (folders.length > 0) draft.folders = folders;
    draft.internalInfoAccountCodeListId = internalInfo.internalInfoAccountCodeListId;
    draft.internalInfoCostCenterCodeListId = internalInfo.internalInfoCostCenterCodeListId;
    draft.internalInfoCostUnitCodeListId = internalInfo.internalInfoCostUnitCodeListId;
    draft.internalInfoProjectCodeListId = internalInfo.internalInfoProjectCodeListId;
  }

  const distribution = mapLineDistribution(detail, ctx);
  if (distribution) draft.distribution = distribution;
  return distribution;
};
